import type {
  AnswerValue,
  MultipleChoiceAnswer,
  OpenTextAnswer,
  Question,
} from '@voice2me/shared';

type Props = {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue | undefined) => void;
  error?: string;
  disabled?: boolean;
};

/**
 * Renders one question from the shared definition. Used by both the employee survey and the
 * editor preview, so an author always sees exactly what their employees will see.
 */
export function QuestionField({ question, value, onChange, error, disabled }: Props) {
  const describedBy = error ? `${question.id}-error` : undefined;

  return (
    <fieldset className="question" aria-invalid={error ? true : undefined}>
      {/* The prompt is a block heading sitting above the answer box, not a caption on its border. */}
      <legend className="question-prompt">
        {/* Only ever empty in the editor preview; publishing requires a prompt. */}
        {question.prompt || <em>Untitled question</em>}
        {question.required && (
          <span className="required" aria-label="required">
            {' '}
            *
          </span>
        )}
      </legend>
      {question.helpText && <p className="help">{question.helpText}</p>}

      <div className="question-body">
      {question.type === 'multiple_choice' && (
        <div className="options" aria-describedby={describedBy}>
          {question.options.map((option) => {
            const selected = ((value as MultipleChoiceAnswer | undefined)?.optionIds ?? []).includes(
              option.id,
            );
            return (
              <label key={option.id} className="option">
                <input
                  type={question.allowMultiple ? 'checkbox' : 'radio'}
                  name={question.id}
                  value={option.id}
                  checked={selected}
                  disabled={disabled}
                  onChange={(event) => {
                    const current = (value as MultipleChoiceAnswer | undefined)?.optionIds ?? [];
                    if (!question.allowMultiple) {
                      onChange({ optionIds: [option.id] });
                      return;
                    }
                    const next = event.target.checked
                      ? [...current, option.id]
                      : current.filter((id) => id !== option.id);
                    onChange(next.length > 0 ? { optionIds: next } : undefined);
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {question.type === 'boolean' && (
        <div className="options options-inline" aria-describedby={describedBy}>
          {[
            { label: 'Yes', boolValue: true },
            { label: 'No', boolValue: false },
          ].map((choice) => (
            <label key={choice.label} className="option">
              <input
                type="radio"
                name={question.id}
                checked={(value as { value?: boolean } | undefined)?.value === choice.boolValue}
                disabled={disabled}
                onChange={() => onChange({ value: choice.boolValue })}
              />
              <span>{choice.label}</span>
            </label>
          ))}
        </div>
      )}

      {question.type === 'rating' && (
        <div className="rating" aria-describedby={describedBy}>
          {question.minLabel && <span className="scale-label">{question.minLabel}</span>}
          <div className="scale">
            {Array.from(
              { length: question.max - question.min + 1 },
              (_unused, index) => question.min + index,
            ).map((point) => (
              <label key={point} className="scale-point">
                <input
                  type="radio"
                  name={question.id}
                  checked={(value as { value?: number } | undefined)?.value === point}
                  disabled={disabled}
                  onChange={() => onChange({ value: point })}
                />
                <span>{point}</span>
              </label>
            ))}
          </div>
          {question.maxLabel && <span className="scale-label">{question.maxLabel}</span>}
        </div>
      )}

      {question.type === 'open_text' && (
        <>
          <textarea
            rows={4}
            maxLength={question.maxLength}
            disabled={disabled}
            aria-describedby={describedBy}
            value={(value as OpenTextAnswer | undefined)?.text ?? ''}
            onChange={(event) =>
              onChange(event.target.value ? { text: event.target.value } : undefined)
            }
          />
          <p className="counter">
            {(value as OpenTextAnswer | undefined)?.text.length ?? 0} / {question.maxLength}
          </p>
        </>
      )}

      {error && (
        <p className="field-error" id={`${question.id}-error`} role="alert">
          {error}
        </p>
      )}
      </div>
    </fieldset>
  );
}
